import React from 'react';
import {
  Dialog,
  DialogContent,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { CheckCircle2, XCircle, Crown, ArrowRight, RefreshCw } from 'lucide-react';

interface PaymentResultDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  success: boolean;
  onNavigateToDashboard: () => void;
  onRetry: () => void;
}

const PaymentResultDialog: React.FC<PaymentResultDialogProps> = ({
  open,
  onOpenChange,
  success,
  onNavigateToDashboard,
  onRetry,
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md text-center">
        {success ? (
          <>
            {/* Success State */}
            <div className="flex flex-col items-center py-6">
              <div className="relative mb-6">
                <div className="flex h-24 w-24 items-center justify-center rounded-full bg-green-500/10">
                  <CheckCircle2 className="h-12 w-12 text-green-500" />
                </div>
                <div className="absolute -right-2 -top-2 flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-gold to-amber-500 animate-bounce">
                  <Crown className="h-5 w-5 text-gold-foreground" />
                </div>
              </div>
              
              <h2 className="text-2xl font-bold text-foreground mb-2">
                Payment Successful!
              </h2>
              
              <div className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-gold to-amber-500 px-4 py-2 text-sm font-semibold text-gold-foreground mb-4">
                <Crown className="h-4 w-4" />
                You're now a VIP Member!
              </div>
              
              <p className="text-muted-foreground mb-6">
                Congratulations! You now have full access to all premium notes. Start your preparation journey now.
              </p>

              <Button 
                variant="hero" 
                size="lg" 
                className="w-full gap-2"
                onClick={onNavigateToDashboard}
              >
                Go to Dashboard
                <ArrowRight className="h-4 w-4" />
              </Button>
            </div>
          </>
        ) : (
          <>
            {/* Failure State */}
            <div className="flex flex-col items-center py-6">
              <div className="mb-6 flex h-24 w-24 items-center justify-center rounded-full bg-red-500/10">
                <XCircle className="h-12 w-12 text-red-500" />
              </div>
              
              <h2 className="text-2xl font-bold text-foreground mb-2">
                Payment Failed
              </h2>
              
              <p className="text-muted-foreground mb-6">
                Unfortunately, your payment could not be processed. Please try again or use a different payment method.
              </p>

              <div className="w-full space-y-3">
                <Button 
                  variant="hero" 
                  size="lg" 
                  className="w-full gap-2"
                  onClick={onRetry}
                >
                  <RefreshCw className="h-4 w-4" />
                  Try Again
                </Button>
                
                <Button 
                  variant="outline" 
                  size="lg" 
                  className="w-full"
                  onClick={() => onOpenChange(false)}
                >
                  Cancel
                </Button>
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default PaymentResultDialog;
